import React, { useEffect, useMemo, useState } from 'react';
import PlateformService from '../../components/PlateformService';
import { toast } from 'react-toastify';

const defaultForm = {
  plateform_id: '',
  page_number: 1,
  page_size: 10,
  order_statut: '',
  date_debut: '',
  date_fin: '',
};

const isMarjanePlateform = (plateform) => {
  const type = `${plateform?.type ?? ''}`.toLowerCase();
  const name = `${plateform?.name ?? ''}`.toLowerCase();
  return type.includes('marjane') || name.includes('marjane');
};

const MarjaneMallOrdersSync = () => {
  const [plateforms, setPlateforms] = useState([]);
  const [formState, setFormState] = useState(defaultForm);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const isValidDateInput = (value) => {
    if (!value) return false;
    const d = new Date(value);
    return !Number.isNaN(d.getTime());
  };

  useEffect(() => {
    const loadPlateforms = async () => {
      try {
        const data = await PlateformService.fetchPlateforms();
        setPlateforms(data?.response || data || []);
      } catch (error) {
        console.error(error);
        toast.error('Impossible de charger les plateformes Marjane Mall.');
      }
    };

    loadPlateforms();
  }, []);

  const marjanePlateforms = useMemo(
    () => (Array.isArray(plateforms) ? plateforms : []).filter(isMarjanePlateform),
    [plateforms]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleSync = async (useByDate = false) => {
    const errors = {};
    if (useByDate) {
      if (!formState.date_debut) {
        errors.date_debut = 'La date de début est obligatoire.';
      } else if (!isValidDateInput(formState.date_debut)) {
        errors.date_debut = 'Format de date invalide.';
      }

      if (formState.date_fin && !isValidDateInput(formState.date_fin)) {
        errors.date_fin = 'Format de date invalide.';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSyncing(true);
    setLastResult(null);
    try {
      const toIso = (v) => (v ? new Date(v).toISOString() : undefined);

      const payload = {
        plateform_id: formState.plateform_id || undefined,
        date_debut: toIso(formState.date_debut),
        date_fin: toIso(formState.date_fin),
        page_number: Number(formState.page_number) || undefined,
        page_size: Number(formState.page_size) || undefined,
        order_statut: formState.order_statut || undefined,
      };

      const response = useByDate
        ? await PlateformService.syncMarjaneMallOrdersByDate(payload)
        : await PlateformService.syncMarjaneMallOrders(payload);
      setLastResult(response);
      toast.success(response?.message || 'Synchronisation Marjane Mall réussie.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Erreur lors de la synchronisation Marjane Mall.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="mb-4">
        <h2 className="h4">Synchronisation des commandes Marjane Mall</h2>
        <p className="text-muted">
          Lancez une synchronisation pour importer les commandes manquantes depuis Marjane Mall.
        </p>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Plateforme Marjane Mall</label>
              <select
                className="form-select"
                name="plateform_id"
                value={formState.plateform_id}
                onChange={handleChange}
              >
                <option value="">Automatique (première Marjane Mall)</option>
                {marjanePlateforms.map((plateform) => (
                  <option key={plateform._id || plateform.id} value={plateform._id || plateform.id}>
                    {plateform.name || plateform.login || plateform._id || plateform.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Page</label>
              <input
                type="number"
                className="form-control"
                name="page_number"
                min="1"
                value={formState.page_number}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Taille</label>
              <input
                type="number"
                className="form-control"
                name="page_size"
                min="1"
                value={formState.page_size}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Statut (optionnel)</label>
              <input
                type="text"
                className="form-control"
                name="order_statut"
                placeholder="Ex: shipped"
                value={formState.order_statut}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Date de début</label>
              <input
                type="datetime-local"
                className={`form-control ${formErrors.date_debut ? 'is-invalid' : ''}`}
                name="date_debut"
                placeholder="Sélectionnez date et heure"
                value={formState.date_debut}
                onChange={handleChange}
              />
              {formErrors.date_debut && (
                <div className="invalid-feedback">{formErrors.date_debut}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Date de fin (optionnel)</label>
              <input
                type="datetime-local"
                className={`form-control ${formErrors.date_fin ? 'is-invalid' : ''}`}
                name="date_fin"
                placeholder="Sélectionnez date et heure"
                value={formState.date_fin}
                onChange={handleChange}
              />
              {formErrors.date_fin && (
                <div className="invalid-feedback">{formErrors.date_fin}</div>
              )}
            </div>
          </div>

          <div className="mt-4 d-flex gap-2">
            <button
              className="btn btn-primary"
              onClick={() => handleSync(false)}
              disabled={syncing}
            >
              {syncing ? 'Synchronisation…' : 'Synchroniser'}
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => handleSync(true)}
              disabled={syncing}
              type="button"
            >
              {syncing ? 'Synchronisation…' : 'Synchroniser par date'}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setFormState(defaultForm);
                setFormErrors({});
                setLastResult(null);
              }}
              type="button"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {lastResult && (
        <div className="card mt-4">
          <div className="card-body">
            <h5 className="card-title">Dernier résultat</h5>
            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarjaneMallOrdersSync;
